/**
 * Number helpers shared across the app and website.
 *
 * Canonical home for `localeRound` and `roundToTwoDecimals`. Both used to live
 * (slightly differently) in each app's `lib/utils/math.ts` — the website's
 * `localeRound` had a `|| 2` fallback that misbehaved for `decimals: 0`; this
 * file is the de-duplicated, correct version. The per-app `math.ts` files now
 * re-export from here so existing call sites don't need to change.
 */

export const roundToTwoDecimals = (num: number) =>
	Math.round((num + Number.EPSILON) * 100) / 100;

/**
 * Formats a number with Norwegian (`nb`) locale by default — comma decimal,
 * thin-space grouping. Pass `lang` to override (e.g. for the multilingual app).
 *
 * `decimals` controls maximum fraction digits, `minDecimals` the minimum
 * (defaults to the same value, so output has a fixed number of digits). Both
 * use nullish-coalescing so `decimals: 0` works correctly.
 */
export const localeRound = (
	num: number,
	opt?: { decimals?: number; minDecimals?: number; lang?: string },
) =>
	new Intl.NumberFormat(opt?.lang || "nb", {
		maximumFractionDigits: opt?.decimals ?? 2,
		minimumFractionDigits: opt?.minDecimals ?? opt?.decimals ?? 2,
	}).format(num);
